import React, { useState } from 'react';
import './TabModal.scss';
import { createPortal } from 'react-dom';
import ModalHeader from '../ModalHeader';
import ActionButtons from '../ActionButtons';
import { ReactComponent as AlertIcon } from '../../../images/alert-circle.svg';

interface Tab {
  id: string;
  name: string;
}

interface EnhancedTab extends Tab {
  hasError?: boolean;
}

interface EntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  onSave?: () => void;
  tabs?: Tab[];
  enhancedTabs?: EnhancedTab[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  children?: React.ReactNode;
  isSubmitting?: boolean;
  disableSubmit?: boolean;
  customHeaderContent?: React.ReactNode;
}

const EntityModal: React.FC<EntityModalProps> = ({
  isOpen,
  onClose,
  title,
  onSave,
  tabs = [],
  enhancedTabs,
  activeTab: controlledActiveTab,
  onTabChange,
  children,
  isSubmitting = false,
  disableSubmit = false,
  customHeaderContent,
}) => {
  const [internalActiveTab, setInternalActiveTab] = useState<string>(tabs.length > 0 ? tabs[0].id : '');
  const activeTab = controlledActiveTab !== undefined ? controlledActiveTab : internalActiveTab;
  const tabsToRender = enhancedTabs || tabs;

  const handleSave = () => {
    if (onSave) {
      onSave();
    }
  };

  const handleTabClick = (tabId: string) => {
    if (onTabChange) {
      onTabChange(tabId);
    } else {
      setInternalActiveTab(tabId);
    }
  };

  return isOpen
    ? createPortal(
        <div className='modal-overlay entity-modal-overlay'>
          <div className='modal-container'>
            <div className='modal-main-container'>
              <ModalHeader title={title} onClose={onClose} border={true} customContent={customHeaderContent} />
              <div className='entity-modal-main-container' style={{ flex: 1 }}>
                <div className='main-content entity-modal-main-content'>
                  <div className='tabs-layout'>
                    {/* Left Sidebar with Tabs */}
                    <div className='tabs-sidebar'>
                      {tabsToRender.map((tab) => (
                        <div
                          key={tab.id}
                          className={`tab-item ${activeTab === tab.id ? 'active' : ''}`}
                          onClick={() => handleTabClick(tab.id)}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {tab.name}
                            {(tab as EnhancedTab).hasError && <AlertIcon style={{ width: '18px', height: '18px' }} />}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Content Area */}
                    <div className='tab-content'>
                      {children && (
                        <div className='content-section' style={{ height: '100%' }}>
                          {children}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* Footer with Action Buttons */}
              <div className='entity-modal-footer' style={{ flexShrink: 0 }}>
                <ActionButtons
                  onCancel={onClose}
                  onSubmit={handleSave}
                  buttonText='Save'
                  disableSubmit={disableSubmit}
                  isSubmitting={isSubmitting}
                />
              </div>
            </div>
          </div>
        </div>,
        document.querySelector('.outlet-container') || document.body,
      )
    : null;
};

export default EntityModal;
